const PLAID_PENDING_STATUSES = new Set(['PENDING', '']);

/**
 * Returns true when the tenant has a pending application change request
 * that they haven't submitted yet.
 *
 * Rules:
 * - Must be in `changeRequest` status
 * - `timestampCompleted` must be null/undefined — i.e. application changes NOT yet submitted
 * - Works for both "Application only" (isNewIdvRequired=false) AND
 *   "Both Application and IDV" (isNewIdvRequired=true) change request types.
 *   The isNewIdvRequired flag only determines whether the tenant needs to do IDV *after*
 *   submitting the application changes, not whether to show the change request screen.
 *
 * Note: The Plaid session status is not checked here because we want to show
 * the change-request screen regardless of the Plaid session state.
 */
export const shouldShowIdvChangeRequest = (data: {
  tenantIdvStatus?: string | null;
  timestampCompleted?: string | Date | null;
}): boolean => {
  if (data.tenantIdvStatus !== 'changeRequest') {
    return false;
  }
  // If timestampCompleted is set, the tenant already submitted their application changes
  if (data.timestampCompleted != null) {
    return false;
  }
  return true;
};

/**
 * Legacy helper kept for the Plaid-status-aware IDV polling path used in
 * IdentityVerification.tsx, where we want to avoid re-showing the change-request
 * screen if the tenant already completed the Plaid session.
 */
export const shouldShowIdvChangeRequestPolling = (data: {
  tenantIdvStatus?: string | null;
  status?: string | null;
  isNewIdvRequired?: boolean | number | string | null;
}): boolean => {
  if (data.tenantIdvStatus !== 'changeRequest') {
    return false;
  }
  const plaidStatus = (data.status ?? '').trim().toUpperCase();
  if (!plaidStatus || PLAID_PENDING_STATUSES.has(plaidStatus)) {
    return false;
  }
  return true;
};
